import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import { Save, ArrowLeft, Settings as SettingsIcon, FileJson, Database, GitBranch, List, Bot, Send } from 'lucide-react';
import ImprovedScriptEditor from '../components/ImprovedScriptEditor';
import FlowchartEditor from '../components/FlowchartEditor';
import TagsEditor from '../components/TagsEditor';
import KBConstructor from '../components/KBConstructor';
import BotRoleEditor from '../components/BotRoleEditor';
import { useAuthStore } from '../store/authStore';

export default function ProjectEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('bot_role');
  const [scriptViewMode, setScriptViewMode] = useState('editor');
  const scriptEditorRef = React.useRef(null);
  
  const isAdmin = user?.Role === 'ADMIN';

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/control_panel/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки проекта');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Если в режиме скрипта и редактора, проверяем есть ли функция для получения текущего скрипта
      if (activeTab === 'script' && scriptViewMode === 'editor' && scriptEditorRef.current?.getCurrentScript) {
        const currentScript = scriptEditorRef.current.getCurrentScript();
        if (currentScript) {
          setProject(prev => ({ ...prev, Script: currentScript }));
          // Используем currentScript для сохранения
          const { ID, User_ID, Created_At, Updated_At, Is_Active, User, ...updateData } = { ...project, Script: currentScript };
          await api.put(`/control_panel/${id}`, updateData);
          toast.success('Проект сохранен');
          setIsSaving(false);
          return;
        }
      }
      
      // Стандартное сохранение для других случаев
      const { ID, User_ID, Created_At, Updated_At, Is_Active, User, ...updateData } = project;
      await api.put(`/control_panel/${id}`, updateData);
      toast.success('Проект сохранен');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Ошибка сохранения проекта');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = () => {
    const telegramChannel = project.Script?.Settings?.Telegram_Channel;
    if (!telegramChannel) {
      toast.error('Пожалуйста, укажите Telegram канал в настройках');
      return;
    }
    
    const message = `/I_want_to_test_${project.Script_Name}_in_Russian`;
    const telegramUrl = `https://t.me/${telegramChannel.replace('@', '')}?text=${encodeURIComponent(message)}`;
    window.open(telegramUrl, '_blank');
  };

  const updateProject = (field, value) => {
    setProject(prev => ({ ...prev, [field]: value }));
  };

  const handleScriptChange = (newScript, newNodePositions) => {
    setProject(prev => ({
      ...prev,
      Script: newScript,
      Node_Positions: newNodePositions || prev.Node_Positions || {}
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'bot_role', label: 'Роль бота', icon: Bot },
    { id: 'script', label: 'Скрипт', icon: FileJson },
    { id: 'kb', label: 'База знаний', icon: Database },
    { id: 'settings', label: 'Настройки', icon: SettingsIcon },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{project.Script_Name}</h1>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={handleTest} 
            variant="outline"
            disabled={!project.Script?.Settings?.Telegram_Channel}
          >
            <Send className="h-4 w-4 mr-2" />
            TEST
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex space-x-2 border-b">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {activeTab === 'bot_role' && (
            <BotRoleEditor
              botRole={project.Script?.Settings?.Bot_Role || []}
              onChange={(value) => updateProject('Script', {
                ...project.Script,
                Settings: { ...project.Script?.Settings, Bot_Role: value }
              })}
            />
          )}

          {activeTab === 'script' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-sm font-semibold text-gray-700">Режим отображения</h3>
                <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setScriptViewMode('editor')}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      scriptViewMode === 'editor'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <List className="h-4 w-4" />
                    <span>Редактор</span>
                  </button>
                  <button
                    onClick={() => setScriptViewMode('flowchart')}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      scriptViewMode === 'flowchart'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <GitBranch className="h-4 w-4" />
                    <span>Блок-схема</span>
                  </button>
                </div>
              </div>

              {scriptViewMode === 'editor' ? (
                <ImprovedScriptEditor
                  ref={scriptEditorRef}
                  script={project.Script}
                  onChange={(value) => updateProject('Script', value)}
                  onSave={handleSave}
                />
              ) : (
                <FlowchartEditor
                  script={project.Script}
                  nodePositions={project.Node_Positions || {}}
                  onChange={handleScriptChange}
                />
              )}
            </div>
          )}

          {activeTab === 'kb' && (
            <KBConstructor 
              projectId={project.ID}
              onSave={fetchProject}
            />
          )}
          
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Название проекта</label>
                <Input
                  value={project.Script_Name}
                  onChange={(e) => updateProject('Script_Name', e.target.value)}
                  placeholder="Название проекта"
                />
              </div>

              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name</label>
                  <Input
                    value={project.Company_Name || ''}
                    onChange={(e) => updateProject('Company_Name', e.target.value)}
                    placeholder="Название компании"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center space-x-2">
                  <Send className="h-4 w-4" />
                  <span>Telegram канал</span>
                </label>
                <Input
                  value={project.Script?.Settings?.Telegram_Channel || ''}
                  onChange={(e) => updateProject('Script', {
                    ...project.Script,
                    Settings: { 
                      ...project.Script?.Settings, 
                      Telegram_Channel: e.target.value 
                    }
                  })}
                  placeholder="@channel_name или channel_name"
                />
              </div>

              <TagsEditor
                label="Теги включения (Have Tags)"
                tags={project.Have_Tags || []}
                onChange={(tags) => updateProject('Have_Tags', tags)}
              />

              <TagsEditor
                label="Теги исключения (Not Have Tags)"
                tags={project.Not_Have_Tags || []}
                onChange={(tags) => updateProject('Not_Have_Tags', tags)}
              />

              <div>
                <label className="block text-sm font-medium mb-2">Sources (JSON)</label>
                <textarea
                  value={JSON.stringify(project.Sources || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      updateProject('Sources', JSON.parse(e.target.value));
                    } catch (err) {}
                  }}
                  className="w-full h-32 p-3 border rounded-md font-mono text-sm"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
